import React from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';

import { useStyles } from './ProcedureExecutionsGraph.styles'

import { useExecutionsGridUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import {
    ResponsiveContainer,
    XAxis,
    YAxis,
    CartesianGrid,
    ComposedChart,
    Line,
} from 'recharts';

const STROKE_WIDTH = 1.8;
const ACTIVE_STROKE_WIDTH = 3;
const COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6'];

type ExecutionChartPoint = {
    plotIndex: number;
    stepLabel: string;
    [key: string]: any;
};

type ExecutionMeta = {
    key: string;
    executionId: string;
    dataKey: string;
    color: string;
    label: string;
    stepCount: number;
    endPlotIndex: number;
    endTime: number;
};

type ExecutionSeries = {
    key: string;
    executionId: string;
    dataKey: string;
    color: string;
    label: string;
    stepCount: number;
};

type ChartExecution = {
    id: string;
    startTime: string | null;
    steps: Array<unknown>;
};

function timeFormatterDuration(value: number | undefined): string {
    const s = Math.max(0, Math.floor(value!));

    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;

    const pad2 = (n: number) => String(n).padStart(2, "0");

    return `${pad2(minutes)}:${pad2(seconds)}`;
}

function formatExecutionLabel(startTime: string | null | undefined, fallbackIndex: number): string {
    if (!startTime) {
        return `Execution ${fallbackIndex}`;
    }

    const date = new Date(startTime);

    if (Number.isNaN(date.getTime())) {
        return `Execution ${fallbackIndex}`;
    }

    return date.toLocaleString('en-GB', {
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getSeriesOpacity(hoveredKey: string | null, key: string): number {
    if (!hoveredKey) {
        return 0.92;
    }

    return hoveredKey === key ? 1 : 0.22;
}

function isNumericValue(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

type ProcedureExecutionsGraphProps = {
    hoveredExecutionId?: string | null;
    onHoveredExecutionIdChange?: (executionId: string | null) => void;
};

export const ProcedureExecutionsGraph: React.FC<ProcedureExecutionsGraphProps> = ({
    hoveredExecutionId = null,
    onHoveredExecutionIdChange,
}) =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const executionsGridUIStore = useExecutionsGridUIStore();

    return (
        <>
            <Observer>
                {() => {
                    const isLoading = executionsGridUIStore.isExecutionsLoading || executionsGridUIStore.status.isLoading;
                    const executionSeries: ExecutionSeries[] = executionsGridUIStore.chartExecutions.map((execution: ChartExecution, index: number) => ({
                        key: `exe${index + 1}`,
                        executionId: execution.id,
                        dataKey: `exe${index + 1}Time`,
                        color: COLORS[index % COLORS.length],
                        label: formatExecutionLabel(execution.startTime, index + 1),
                        stepCount: execution.steps?.length ?? 0,
                    }));

                    const formattedExecutionPoints = executionsGridUIStore.formattedExecutions as ExecutionChartPoint[];

                    const realData = formattedExecutionPoints.map((point, pointIndex) => {
                        const nextPoint: ExecutionChartPoint = {
                            plotIndex: pointIndex,
                            stepLabel: pointIndex === 0 ? '' : String(point.index ?? pointIndex),
                        };

                        executionSeries.forEach((series: ExecutionSeries) => {
                            const seriesValue = point?.[series.key]?.time;
                            nextPoint[series.dataKey] = isNumericValue(seriesValue) ? seriesValue : null;
                        });

                        return nextPoint;
                    });

                    const executionMeta = executionSeries
                        .map((series: ExecutionSeries): ExecutionMeta | null => {
                            const lastPoint = [...realData]
                                .reverse()
                                .find(point => isNumericValue(point?.[series.dataKey]));

                            const endTime = lastPoint?.[series.dataKey];
                            const endPlotIndex = Number(lastPoint?.plotIndex);

                            if (!isNumericValue(endTime) || !Number.isFinite(endPlotIndex)) {
                                return null;
                            }

                            return {
                                ...series,
                                endPlotIndex,
                                endTime,
                            };
                        })
                        .filter(Boolean) as ExecutionMeta[];

                    const activeHoveredExecutionId = hoveredExecutionId && executionMeta.some(item => item.executionId === hoveredExecutionId)
                        ? hoveredExecutionId
                        : null;
                    const xTicks = realData.map(point => point.plotIndex);

                    return (
                        <div className={styles.graphContainer}>
                            <div className={styles.headerWraper}>
                                <div className={styles.headerText}>
                                    <div>Performance Comparison</div>
                                    <div className={styles.headerCaption}>
                                        Hover a line or end marker to focus one execution. Lower duration indicates better performance.
                                    </div>
                                </div>
                            </div>
                            {isLoading ? (
                                <div className={styles.emptyState}>
                                    Loading executions...
                                </div>
                            ) : realData.length > 1 && executionMeta.length > 0 ? (
                                <div className={styles.chartSection}>
                                    <div className={styles.chartContainer}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart
                                                data={realData}
                                                margin={{ top: 12, right: 24, left: 12, bottom: 14 }}
                                            >
                                                <CartesianGrid stroke={theme.colors.border_subtle} strokeDasharray="4 4" vertical={false} />
                                                <XAxis
                                                    xAxisId="bottom"
                                                    orientation="bottom"
                                                    type="number"
                                                    dataKey="plotIndex"
                                                    domain={[0, Math.max(realData.length - 1, 0)]}
                                                    ticks={xTicks}
                                                    height={32}
                                                    tickMargin={8}
                                                    padding={{ left: 0, right: 0 }}
                                                    allowDecimals={false}
                                                    tickFormatter={(value: number) => {
                                                        const point = realData.find(item => item.plotIndex === value);

                                                        return point?.stepLabel ?? '';
                                                    }}
                                                />
                                                <YAxis
                                                    width={56}
                                                    padding={{ top: 10, bottom: 12 }}
                                                    tickFormatter={timeFormatterDuration}
                                                />
                                                {executionMeta.map(execution => (
                                                    <Line
                                                        key={execution.key}
                                                        xAxisId="bottom"
                                                        connectNulls
                                                        dot={false}
                                                        activeDot={{ r: 5 }}
                                                        type="monotone"
                                                        animationDuration={600}
                                                        dataKey={execution.dataKey}
                                                        stroke={execution.color}
                                                        strokeWidth={activeHoveredExecutionId === execution.executionId ? ACTIVE_STROKE_WIDTH : STROKE_WIDTH}
                                                        strokeOpacity={getSeriesOpacity(activeHoveredExecutionId, execution.executionId)}
                                                        name={execution.label}
                                                        isAnimationActive={false}
                                                        onMouseEnter={() => onHoveredExecutionIdChange?.(execution.executionId)}
                                                        onMouseLeave={() => onHoveredExecutionIdChange?.(null)}
                                                    />
                                                ))}
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className={styles.legend}>
                                        {executionMeta.map(execution => (
                                            <button
                                                key={`legend-${execution.key}`}
                                                type="button"
                                                className={classNames(
                                                    styles.legendItem,
                                                    activeHoveredExecutionId === execution.executionId && styles.legendItemActive
                                                )}
                                                style={{ opacity: getSeriesOpacity(activeHoveredExecutionId, execution.executionId) }}
                                                onMouseEnter={() => onHoveredExecutionIdChange?.(execution.executionId)}
                                                onMouseLeave={() => onHoveredExecutionIdChange?.(null)}
                                            >
                                                <span
                                                    className={styles.legendSwatch}
                                                    style={{ backgroundColor: execution.color }}
                                                />
                                                <span className={styles.legendLabel}>{`${execution.label} • ${execution.stepCount} steps`}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.emptyState}>
                                    Not enough executions available to compare this procedure.
                                </div>
                            )}
                        </div>
                    );
                }}
            </Observer>
        </>
    )
}